/**
 * LeftoverWarehouseModal — shown when completing a direct trip with leftover goods.
 * Allows user to select a warehouse to receive the leftover items or skip.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Warehouse as WarehouseIcon, AlertTriangle } from 'lucide-react';
import { LeftoverItem } from '../../utils/trip-leftover-calculator';
import { Warehouse } from '../../types/entities';
import { warehouseAPI } from '../../api/warehouses';

interface LeftoverWarehouseModalProps {
  isOpen: boolean;
  leftovers: LeftoverItem[];
  onConfirm: (warehouseId: number) => void;
  onSkip: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const formatQty = (qty: number) =>
  new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 3 }).format(qty);

export const LeftoverWarehouseModal: React.FC<LeftoverWarehouseModalProps> = ({
  isOpen,
  leftovers,
  onConfirm,
  onSkip,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingWarehouses(true);
    warehouseAPI
      .getAll({ status: 'ACTIVE' })
      .then((data) => {
        setWarehouses(data);
        if (data.length > 0) setSelectedWarehouseId(data[0].id);
      })
      .catch(console.error)
      .finally(() => setLoadingWarehouses(false));
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <h2 className="text-base font-semibold text-gray-800">
            {t('trips.leftover.modalTitle')}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">{t('trips.leftover.modalDesc')}</p>

          {/* Leftover items table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">{t('trips.itemName')}</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">{t('trips.leftover.purchaseQty')}</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">{t('trips.leftover.deliveredQty')}</th>
                  <th className="text-right px-3 py-2 font-medium text-amber-600">{t('trips.leftover.leftoverQty')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {leftovers.map((item) => (
                  <tr key={item.itemName}>
                    <td className="px-3 py-2 text-gray-800">{item.itemName}</td>
                    <td className="px-3 py-2 text-right text-gray-600">
                      {formatQty(item.purchaseQty)} {item.unit}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">
                      {formatQty(item.saleQty)} {item.unit}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-amber-600">
                      {formatQty(item.leftoverQty)} {item.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Warehouse selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <WarehouseIcon className="w-4 h-4 inline mr-1" />
              {t('trips.leftover.selectWarehouse')}
            </label>
            {loadingWarehouses ? (
              <div className="text-sm text-gray-400">{t('common.loading')}</div>
            ) : (
              <select
                className="input-field w-full"
                value={selectedWarehouseId ?? ''}
                onChange={(e) => setSelectedWarehouseId(Number(e.target.value))}
              >
                {warehouses.map((wh) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.name} ({wh.code})
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-between gap-3">
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={onCancel}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary text-sm text-amber-600 border-amber-300 hover:bg-amber-50"
              onClick={onSkip}
              disabled={loading}
            >
              {t('trips.leftover.skipBtn')}
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => selectedWarehouseId && onConfirm(selectedWarehouseId)}
              disabled={loading || !selectedWarehouseId}
            >
              {loading ? t('common.saving') : t('trips.leftover.confirmBtn')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
